import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
/** Server-backed proposal worklists with reviewable follow-up actions. */
import { useEffect, useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  Dropdown,
  FormControl,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import toast from "react-hot-toast";
import { apiClient, downloadApiBlob } from "../../lib/api";
import { proposalsApi } from "../../lib/proposalsApi";
import {
  formatCalendarDate,
  isCalendarDatePast,
  localCalendarDate,
} from "../../lib/calendarDate";
import { useDebounce } from "../../hooks/useDebounce";
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from "../../components/common/ResponsiveTable";
import type { ProposalListItem, ProposalStatus } from "../../types/proposals";
import { proposalCsvCell, updateProposalParams } from "./proposalListHelpers";

type Row = ProposalListItem & {
  offered_low?: string | null;
  offered_high?: string | null;
  accepted_value?: string | null;
};
type ReviewedRow = Row & {
  recipient: string;
  review_version: string;
  can_remind: boolean;
  renewal: boolean;
  reason: string;
};
type Preferences = {
  columns: string[];
  dense: boolean;
  views: { name: string; query: string }[];
  lastQuery?: string;
};
const defaultPreferences: Preferences = {
  columns: [
    "proposal_number",
    "contact",
    "status",
    "owner",
    "sent_at",
    "viewed_at",
    "valid_until",
    "offered_low",
    "accepted_value",
  ],
  dense: false,
  views: [],
};
const statuses: ProposalStatus[] = [
  "draft",
  "sent",
  "viewed",
  "accepted",
  "declined",
  "expired",
];
const money = (value: unknown) =>
  value == null
    ? "—"
    : Number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
const timestamp = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "—";
const statusColor = (value: string) =>
  value === "accepted"
    ? "success"
    : value === "declined"
      ? "danger"
      : value === "viewed"
        ? "warning"
        : "neutral";
const label = (value: string) =>
  value.replaceAll("_", " ").replace(/^./, (first) => first.toUpperCase());

function ProposalPreview({
  id,
  close,
}: {
  id: number | null;
  close: () => void;
}) {
  const detail = useQuery({
    queryKey: ["proposal-preview", id],
    enabled: !!id,
    queryFn: () => proposalsApi.get(id!),
  });
  const activity = useQuery({
    queryKey: ["proposal-preview-events", id],
    enabled: !!id,
    queryFn: async () =>
      (
        await apiClient.get<{
          events: { id: number; description: string; created_at: string }[];
        }>(`/api/proposals/${id}/workflow/`)
      ).data,
  });
  return (
    <Drawer open={!!id} onClose={close} anchor="right" size="md">
      <Box sx={{ p: 3, overflow: "auto" }}>
        <Button variant="plain" onClick={close}>
          Close preview
        </Button>
        {detail.isPending ? (
          <CircularProgress />
        ) : detail.isError || !detail.data ? (
          <Alert color="danger">
            Preview unavailable.{" "}
            <Button onClick={() => detail.refetch()}>Retry</Button>
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Typography level="h3">{detail.data.proposal_number}</Typography>
            <Typography level="title-lg">{detail.data.title}</Typography>
            <Typography
              component={Link}
              to={`/crm/contacts/${detail.data.contact}`}
            >
              {detail.data.contact_name}
            </Typography>
            <Typography
              component={Link}
              to={`/crm/opportunities/${detail.data.opportunity}`}
            >
              {detail.data.opportunity_name}
            </Typography>
            {detail.data.tiers.map((tier) => (
              <Card key={tier.id}>
                <Typography level="title-md">{tier.name}</Typography>
                <Typography>{money(tier.total_amount)}</Typography>
              </Card>
            ))}
            <Typography level="title-md">Latest activity</Typography>
            {activity.isError ? (
              <Alert color="warning">Activity unavailable.</Alert>
            ) : (
              activity.data?.events.slice(0, 5).map((event) => (
                <Typography key={event.id}>
                  {event.description} · {timestamp(event.created_at)}
                </Typography>
              ))
            )}
            <Button component={Link} to={`/crm/proposals/${id}`}>
              Open full proposal
            </Button>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

export default function ProposalList() {
  const navigate = useNavigate(),
    client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const queryString = params.toString();
  const debounced = useDebounce(queryString, 250);
  const changing = debounced !== queryString;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [selected, setSelected] = useState<number[]>([]);
  const [preview, setPreview] = useState<number | null>(null);
  const [opportunitySearch, setOpportunitySearch] = useState("");
  const [preferences, setPreferences] =
    useState<Preferences>(defaultPreferences);
  const [loadedScope, setLoadedScope] = useState("");
  const [viewName, setViewName] = useState("");
  const [batch, setBatch] = useState<ReviewedRow[] | null>(null);
  const [outcomes, setOutcomes] = useState<Record<number, string>>({});
  const [sending, setSending] = useState(false);
  const restoredPosition = useRef(false);
  const rememberPosition = (id: number) => {
    try {
      sessionStorage.setItem(
        `apexos:proposal-position:${loadedScope}`,
        String(id),
      );
    } catch {}
  };
  const update = (
    values: Record<string, string | number | null>,
    reset = true,
    replace = false,
  ) => setParams(updateProposalParams(params, values, reset), { replace });
  const options = useQuery({
    queryKey: [
      "proposal-worklist-options",
      opportunitySearch,
      params.get("opportunity"),
    ],
    queryFn: async () =>
      (
        await apiClient.get<{
          scope: string;
          owners: {
            created_by_id: number;
            created_by__first_name: string;
            created_by__last_name: string;
          }[];
          opportunities: { id: number; name: string }[];
        }>(
          `/api/proposals/worklist-options/?search=${encodeURIComponent(opportunitySearch)}&selected=${params.get("opportunity") || ""}`,
        )
      ).data,
  });
  useEffect(() => {
    const scope = options.data?.scope;
    if (!scope || scope === loadedScope) return;
    let saved: Preferences = defaultPreferences;
    try {
      const stored = JSON.parse(
        localStorage.getItem(`apexos:proposal-list:${scope}`) || "null",
      );
      if (
        stored &&
        Array.isArray(stored.columns) &&
        Array.isArray(stored.views)
      )
        saved = { ...defaultPreferences, ...stored };
    } catch {}
    setPreferences(saved);
    setLoadedScope(scope);
    if (!queryString && saved.lastQuery)
      setParams(new URLSearchParams(saved.lastQuery), { replace: true });
  }, [options.data?.scope, loadedScope]);
  useEffect(() => {
    if (loadedScope)
      try {
        localStorage.setItem(
          `apexos:proposal-list:${loadedScope}`,
          JSON.stringify({ ...preferences, lastQuery: queryString }),
        );
      } catch {}
  }, [preferences, queryString, loadedScope]);
  useEffect(() => {
    setSelected([]);
  }, [queryString]);
  const listing = useQuery({
    queryKey: ["proposals", "worklist", debounced],
    queryFn: async () =>
      (
        await apiClient.get<{ count: number; page: number; results: Row[] }>(
          `/api/proposals/?${debounced}`,
        )
      ).data,
  });
  const counts = useQuery({
    queryKey: ["proposals", "status-counts"],
    queryFn: () => proposalsApi.statusCounts(),
  });
  const report = useQuery({
    queryKey: ["proposals", "worklist-report", debounced],
    queryFn: async () =>
      (
        await apiClient.get<{
          count: number;
          offered_low: string | null;
          offered_high: string | null;
          accepted_value: string | null;
          accepted_count: number;
          accepted_priced_count: number;
          priced_proposals: number;
          basis: string;
        }>(`/api/proposals/worklist-report/?${debounced}`)
      ).data,
  });
  useEffect(() => {
    if (!changing && listing.data?.page && listing.data.page !== page)
      update({ page: listing.data.page }, false, true);
  }, [listing.data?.page, page, changing]);
  const refresh = () => client.invalidateQueries({ queryKey: ["proposals"] });
  const remove = useMutation({
    mutationFn: (id: number) => proposalsApi.delete(id),
    onSuccess: () => {
      refresh();
      toast.success("Proposal deleted");
    },
    onError: (error: any) =>
      toast.error(
        error?.responseData?.error || "Could not delete this proposal.",
      ),
  });
  const duplicate = useMutation({
    mutationFn: (id: number) => proposalsApi.duplicate(id),
    onSuccess: (proposal) => {
      refresh();
      navigate(`/crm/proposals/${proposal.id}/edit`);
    },
    onError: () => toast.error("Could not duplicate proposal."),
  });
  const pdf = useMutation({
    mutationFn: async (id: number) => {
      const result = await proposalsApi.generatePdf(id);
      if (!result.pdf_url)
        throw new Error(result.message || "Paperwork is being prepared.");
      if (result.pdf_url.startsWith("/api/"))
        await downloadApiBlob(result.pdf_url, `Proposal_${id}.pdf`);
      else window.open(result.pdf_url, "_blank", "noopener,noreferrer");
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const review = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post<{ count: number; results: ReviewedRow[] }>(
          "/api/proposals/batch-review/",
          { ids: selected },
        )
      ).data,
    onSuccess: (result) => {
      setBatch(result.results);
      setOutcomes({});
    },
    onError: () =>
      toast.error(
        "Selection changed or could not be reviewed. Refresh and select the proposals again.",
      ),
  });
  const rows = listing.data?.results || [];
  useEffect(() => {
    if (restoredPosition.current || !loadedScope || !listing.data || changing)
      return;
    restoredPosition.current = true;
    try {
      const id = sessionStorage.getItem(
        `apexos:proposal-position:${loadedScope}`,
      );
      if (id)
        requestAnimationFrame(() =>
          document
            .querySelector(`[data-proposal-id="${Number(id)}"]`)
            ?.scrollIntoView({ block: "center" }),
        );
    } catch {}
  }, [loadedScope, listing.data, changing]);
  const toggle = (id: number) =>
    setSelected((previous) =>
      previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id],
    );
  const columns: ResponsiveColumn<Row>[] = [
    {
      key: "select",
      width: "42px",
      mobileLabel: "Select",
      header: (
        <Checkbox
          slotProps={{ input: { "aria-label": "Select this page" } }}
          checked={
            rows.length > 0 && rows.every((row) => selected.includes(row.id))
          }
          indeterminate={
            selected.length > 0 &&
            !rows.every((row) => selected.includes(row.id))
          }
          onChange={(event) =>
            setSelected(event.target.checked ? rows.map((row) => row.id) : [])
          }
        />
      ),
      render: (row) => (
        <Checkbox
          slotProps={{
            input: { "aria-label": `Select ${row.proposal_number}` },
          }}
          checked={selected.includes(row.id)}
          onChange={() => toggle(row.id)}
        />
      ),
    },
    {
      key: "proposal_number",
      header: "Proposal",
      sortKey: "proposal_number",
      mobileVariant: "header",
      render: (row) => (
        <Box>
          <Typography
            component={Link}
            data-proposal-id={row.id}
            onClick={() => rememberPosition(row.id)}
            to={`/crm/proposals/${row.id}`}
            fontWeight="lg"
          >
            {row.proposal_number}
          </Typography>
          <Typography level="body-xs">{row.title}</Typography>
        </Box>
      ),
    },
    {
      key: "contact",
      header: "Customer",
      sortKey: "contact",
      render: (row) => row.contact_name || "—",
    },
    {
      key: "opportunity",
      header: "Opportunity",
      sortKey: "opportunity",
      render: (row) => row.opportunity_name || "—",
    },
    {
      key: "status",
      header: "Status",
      sortKey: "status",
      render: (row) => (
        <Chip size="sm" color={statusColor(row.status)}>
          {label(row.status)}
        </Chip>
      ),
    },
    {
      key: "owner",
      header: "Sales rep",
      sortKey: "owner",
      render: (row) => row.created_by_name || "Unassigned",
    },
    {
      key: "sent_at",
      header: "Last sent",
      sortKey: "sent_at",
      render: (row) => timestamp(row.sent_at),
    },
    {
      key: "viewed_at",
      header: "First customer view",
      sortKey: "viewed_at",
      render: (row) => timestamp(row.viewed_at),
    },
    {
      key: "date",
      header: "Proposal date",
      sortKey: "date",
      render: (row) => formatCalendarDate(row.proposal_date),
    },
    {
      key: "valid_until",
      header: "Valid through",
      sortKey: "valid_until",
      render: (row) => (
        <Typography
          color={
            isCalendarDatePast(row.valid_until) &&
            !["accepted", "declined"].includes(row.status)
              ? "danger"
              : "neutral"
          }
        >
          {formatCalendarDate(row.valid_until)}
        </Typography>
      ),
    },
    {
      key: "manufacturer",
      header: "Manufacturer",
      sortKey: "manufacturer",
      render: (row) => row.featured_manufacturer_name || "—",
    },
    {
      key: "accepted_tier",
      header: "Accepted package",
      sortKey: "accepted_tier",
      render: (row) => row.accepted_tier_name || "—",
    },
    {
      key: "offered_low",
      header: "Offered packages",
      sortKey: "offered_low",
      render: (row) => `${money(row.offered_low)} – ${money(row.offered_high)}`,
    },
    {
      key: "accepted_value",
      header: "Accepted value",
      sortKey: "accepted_value",
      render: (row) => money(row.accepted_value),
    },
  ];
  const ordering = params.get("ordering") || "-created_at";
  const sendReminders = async () => {
    if (!batch) return;
    setSending(true);
    try {
      for (const row of batch.filter(
        (row) => row.can_remind && outcomes[row.id] !== "Sent",
      )) {
        setOutcomes((previous) => ({ ...previous, [row.id]: "Sending…" }));
        try {
          await proposalsApi.send(
            row.id,
            row.review_version,
            row.recipient.trim(),
          );
          setOutcomes((previous) => ({ ...previous, [row.id]: "Sent" }));
        } catch {
          setOutcomes((previous) => ({
            ...previous,
            [row.id]: "Not confirmed. Review activity before retrying.",
          }));
        }
      }
    } finally {
      setSending(false);
      refresh();
    }
  };
  const exportBatch = () => {
    if (!batch) return;
    const lines = [
      [
        "Proposal",
        "Customer",
        "Opportunity",
        "Status",
        "Recipient",
        "Offered low",
        "Offered high",
        "Accepted value",
      ],
      ...batch.map((row) => [
        row.proposal_number,
        row.contact_name,
        row.opportunity_name,
        row.status,
        row.recipient,
        row.offered_low,
        row.offered_high,
        row.accepted_value,
      ]),
    ];
    const url = URL.createObjectURL(
      new Blob(
        [lines.map((row) => row.map(proposalCsvCell).join(",")).join("\r\n")],
        { type: "text/csv;charset=utf-8" },
      ),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `proposals-${localCalendarDate()}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, width: "100%" }}>
      <Stack
        direction="row"
        gap={1.5}
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography level="h3">Proposals</Typography>
          <Typography>Find the next customer to follow up with.</Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Button
            component={Link}
            to="/crm/proposals/settings"
            variant="outlined"
          >
            Settings
          </Button>
          <Button component={Link} to="/crm/proposals/new">
            New proposal
          </Button>
        </Stack>
      </Stack>
      <Typography level="title-sm">Organization totals</Typography>
      {counts.isError && (
        <Alert
          color="warning"
          endDecorator={
            <Button onClick={() => counts.refetch()}>Retry counts</Button>
          }
        >
          Summary counts are unavailable.
        </Alert>
      )}
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ my: 2 }}>
        {statuses.map((status) => (
          <Button
            key={status}
            variant={params.get("status") === status ? "solid" : "soft"}
            color={statusColor(status)}
            onClick={() =>
              update({
                status: params.get("status") === status ? "" : status,
                worklist: "",
              })
            }
          >
            {label(status)} ·{" "}
            {counts.isPending
              ? "…"
              : counts.isError
                ? "Unavailable"
                : (counts.data?.[status] ?? "—")}
          </Button>
        ))}
      </Stack>
      <CollapsibleFilters><Card sx={{ mb: 2 }}>
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <Input
            placeholder="Search proposals…"
            value={params.get("search") || ""}
            onChange={(event) =>
              update({ search: event.target.value }, true, true)
            }
            sx={{ flex: "1 1 220px" }}
          />
          <Select
            value={params.get("worklist") || ""}
            onChange={(_, value) =>
              update({ worklist: value || "", status: "" })
            }
            sx={{ minWidth: 180 }}
          >
            <Option value="">All proposals</Option>
            <Option value="drafts">Drafts</Option>
            <Option value="awaiting">Awaiting response</Option>
            <Option value="expiring">Expiring in 7 days</Option>
            <Option value="accepted">Accepted</Option>
          </Select>
          <Select
            value={params.get("owner") || ""}
            onChange={(_, value) => update({ owner: value || "" })}
            sx={{ minWidth: 170 }}
          >
            <Option value="">All salespeople</Option>
            <Option value="me">Assigned to me</Option>
            <Option value="none">Unassigned</Option>
            {options.data?.owners.map((owner) => (
              <Option
                key={owner.created_by_id}
                value={String(owner.created_by_id)}
              >
                {`${owner.created_by__first_name} ${owner.created_by__last_name}`.trim() ||
                  `User ${owner.created_by_id}`}
              </Option>
            ))}
          </Select>
          <Autocomplete
            placeholder="Filter opportunity"
            options={options.data?.opportunities || []}
            value={
              options.data?.opportunities.find(
                (item) => String(item.id) === params.get("opportunity"),
              ) || null
            }
            getOptionLabel={(item) => item.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onInputChange={(_, value, reason) => {
              if (reason === "input") setOpportunitySearch(value);
            }}
            onChange={(_, value) => update({ opportunity: value?.id || null })}
            sx={{ minWidth: 220, flex: "1 1 220px" }}
          />
          <FormControl>
            <FormLabel>Proposal date from</FormLabel>
            <Input
              type="date"
              value={params.get("date_from") || ""}
              onChange={(event) => update({ date_from: event.target.value })}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Through</FormLabel>
            <Input
              type="date"
              value={params.get("date_to") || ""}
              onChange={(event) => update({ date_to: event.target.value })}
            />
          </FormControl>
          <Button
            variant="plain"
            onClick={() => {
              const start = new Date();
              start.setDate(start.getDate() - 29);
              update({
                date_from: localCalendarDate(start),
                date_to: localCalendarDate(),
              });
            }}
          >
            Last 30 days
          </Button>
          <Button
            variant="plain"
            onClick={() => setParams(new URLSearchParams())}
          >
            Clear filters
          </Button>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Select
            placeholder="Saved worklists"
            value={null}
            onChange={(_, value) => {
              if (value !== null)
                setParams(
                  new URLSearchParams(preferences.views[Number(value)].query),
                );
            }}
          >
            {preferences.views.map((view, index) => (
              <Option key={index} value={index}>
                {view.name}
              </Option>
            ))}
          </Select>
          <Input
            placeholder="Name this worklist"
            value={viewName}
            onChange={(event) => setViewName(event.target.value)}
          />
          <Button
            variant="soft"
            disabled={!viewName.trim() || !loadedScope}
            onClick={() => {
              setPreferences((previous) => ({
                ...previous,
                views: [
                  ...previous.views.filter(
                    (view) => view.name !== viewName.trim(),
                  ),
                  { name: viewName.trim(), query: queryString },
                ],
              }));
              setViewName("");
            }}
          >
            Save worklist
          </Button>
          <Dropdown>
            <MenuButton>Columns</MenuButton>
            <Menu>
              {columns
                .filter(
                  (column) =>
                    column.key !== "select" && column.key !== "proposal_number",
                )
                .map((column) => (
                  <MenuItem key={column.key}>
                    <Checkbox
                      label={column.header}
                      checked={preferences.columns.includes(column.key)}
                      onChange={(event) =>
                        setPreferences((previous) => ({
                          ...previous,
                          columns: event.target.checked
                            ? [...previous.columns, column.key]
                            : previous.columns.filter(
                                (key) => key !== column.key,
                              ),
                        }))
                      }
                    />
                  </MenuItem>
                ))}
            </Menu>
          </Dropdown>
          <Checkbox
            label="Compact rows"
            checked={preferences.dense}
            onChange={(event) =>
              setPreferences((previous) => ({
                ...previous,
                dense: event.target.checked,
              }))
            }
          />
        </Stack>
      </Card></CollapsibleFilters>
      <Card sx={{ mb: 2 }}>
        <Typography level="title-md">
          Value of matching proposals · selected proposal dates
        </Typography>
        {report.isError ? (
          <Alert
            color="warning"
            endDecorator={
              <Button onClick={() => report.refetch()}>Retry report</Button>
            }
          >
            Value report unavailable.
          </Alert>
        ) : report.isPending || changing ? (
          <Typography>Loading values…</Typography>
        ) : (
          <>
            <Stack direction="row" gap={3} flexWrap="wrap">
              <Typography>Count: {report.data?.count}</Typography>
              <Typography>
                Offered: {money(report.data?.offered_low)} –{" "}
                {money(report.data?.offered_high)}
              </Typography>
              <Typography>
                Accepted: {money(report.data?.accepted_value)} (
                {report.data?.accepted_count} proposals)
              </Typography>
            </Stack>
            <Typography level="body-xs">{report.data?.basis}</Typography>
            {!!report.data &&
              report.data.accepted_priced_count <
                report.data.accepted_count && (
                <Alert color="warning">
                  {report.data.accepted_count -
                    report.data.accepted_priced_count}{" "}
                  accepted proposals lack pricing and are excluded from accepted
                  value.
                </Alert>
              )}
          </>
        )}
      </Card>
      <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography>{selected.length} selected on this page</Typography>
        <Button
          variant="outlined"
          disabled={!selected.length || changing}
          loading={review.isPending}
          onClick={() => review.mutate()}
        >
          Review reminders / export
        </Button>
      </Stack>
      {listing.isError ? (
        <Alert
          color="danger"
          endDecorator={
            <Button onClick={() => listing.refetch()}>Retry list</Button>
          }
        >
          Could not load proposals. Adjust the filters or retry.
        </Alert>
      ) : listing.isPending || changing ? (
        <CircularProgress />
      ) : (
        <Box sx={{
          minWidth: 0,
          "& td, & th": { py: preferences.dense ? 0.5 : 1.25, px: 0.75, whiteSpace: "normal", overflowWrap: "anywhere" },
          "& th button": { whiteSpace: "normal", textAlign: "left" },
          "& .MuiChip-root": { maxWidth: "100%", whiteSpace: "normal" },
          "& td .MuiTypography-root": { overflowWrap: "anywhere" },
          "& td:last-child button": { whiteSpace: "nowrap" },
        }}>
          <ResponsiveTable
            rows={rows}
            actionsWidth="160px"
            cardBreakpoint={1200}
            getRowKey={(row) => row.id}
            columns={columns.filter(
              (column) =>
                column.key === "select" ||
                column.key === "proposal_number" ||
                preferences.columns.includes(column.key),
            )}
            sort={{
              key: ordering.replace(/^-/, ""),
              direction: ordering.startsWith("-") ? "desc" : "asc",
            }}
            onSortChange={(sort) =>
              update({
                ordering: sort
                  ? `${sort.direction === "desc" ? "-" : ""}${sort.key}`
                  : "-created_at",
              })
            }
            rowActions={(row) => (
              <Stack direction="row" gap={0.5}>
                <Button
                  size="sm"
                  variant="plain"
                  onClick={() => {
                    rememberPosition(row.id);
                    setPreview(row.id);
                  }}
                >
                  Preview
                </Button>
                <Dropdown>
                  <MenuButton
                    size="sm"
                    aria-label={`Actions for ${row.proposal_number}`}
                  >
                    More
                  </MenuButton>
                  <Menu>
                    <MenuItem
                      onClick={() => {
                        rememberPosition(row.id);
                        navigate(`/crm/proposals/${row.id}`);
                      }}
                    >
                      Open proposal
                    </MenuItem>
                    {row.status !== "accepted" && (
                      <MenuItem
                        onClick={() =>
                          navigate(`/crm/proposals/${row.id}/edit`)
                        }
                      >
                        Edit
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => duplicate.mutate(row.id)}
                      disabled={duplicate.isPending}
                    >
                      Duplicate
                    </MenuItem>
                    {!["accepted", "declined"].includes(row.status) && (
                      <MenuItem
                        onClick={() =>
                          navigate(`/crm/proposals/${row.id}?review=1`)
                        }
                      >
                        Review and send
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => pdf.mutate(row.id)}
                      disabled={pdf.isPending}
                    >
                      Download PDF
                    </MenuItem>
                    {row.status !== "accepted" && (
                      <MenuItem
                        color="danger"
                        onClick={() => {
                          if (window.confirm(`Delete ${row.proposal_number}?`))
                            remove.mutate(row.id);
                        }}
                      >
                        Delete
                      </MenuItem>
                    )}
                  </Menu>
                </Dropdown>
              </Stack>
            )}
            emptyState={
              <Typography sx={{ p: 3 }}>
                No proposals match these filters.
              </Typography>
            }
          />
        </Box>
      )}
      <Stack
        direction="row"
        gap={1}
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2 }}
      >
        <Typography>
          Page {page} · {listing.data?.count ?? "—"} proposals
        </Typography>
        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            disabled={page <= 1 || changing}
            onClick={() => update({ page: page - 1 }, false)}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            disabled={
              changing ||
              !listing.data ||
              page >= Math.ceil(listing.data.count / 20)
            }
            onClick={() => update({ page: page + 1 }, false)}
          >
            Next
          </Button>
        </Stack>
      </Stack>
      <ProposalPreview id={preview} close={() => setPreview(null)} />
      <Modal
        open={!!batch}
        onClose={sending ? undefined : () => setBatch(null)}
      >
        <ModalDialog
          sx={{
            width: "calc(100% - 24px)",
            maxWidth: 950,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Typography level="h4">
            Review {batch?.length} selected proposals
          </Typography>
          <Typography>
            Reminder emails will go only to eligible recipients below. Expired
            proposals renew for 30 days. Exports include every reviewed row.
          </Typography>
          {batch?.map((row) => (
            <Box
              key={row.id}
              sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Typography level="title-sm">
                {row.proposal_number} · {row.contact_name}
              </Typography>
              <Input
                type="email"
                value={row.recipient}
                placeholder="Recipient email"
                slotProps={{
                  input: {
                    "aria-label": `Recipient email for ${row.proposal_number}`,
                  },
                }}
                disabled={sending || !row.can_remind || !!outcomes[row.id]}
                onChange={(e) =>
                  setBatch(
                    (rows) =>
                      rows?.map((item) =>
                        item.id === row.id
                          ? { ...item, recipient: e.target.value }
                          : item,
                      ) || null,
                  )
                }
                sx={{ my: 1 }}
              />
              <Typography level="body-sm">
                {outcomes[row.id] ||
                  (!row.can_remind
                    ? row.reason
                    : row.renewal
                      ? "Will renew expiration for 30 days"
                      : "Ready for reminder")}
              </Typography>
            </Box>
          ))}
          <Typography level="body-xs">
            “Sent” means the email provider accepted the invitation; it does not
            confirm delivery. For an unconfirmed result, review activity before
            retrying.
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              variant="plain"
              disabled={sending}
              onClick={() => setBatch(null)}
            >
              Close
            </Button>
            <Button variant="outlined" disabled={sending} onClick={exportBatch}>
              Export reviewed CSV
            </Button>
            <Button
              loading={sending}
              disabled={
                !batch?.some((row) => row.can_remind) ||
                batch.some(
                  (row) =>
                    row.can_remind &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.recipient.trim()),
                ) ||
                Object.keys(outcomes).length > 0
              }
              onClick={sendReminders}
            >
              Send {batch?.filter((row) => row.can_remind).length || 0}{" "}
              reminders
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
